/**
 * Angular dependencies
 */
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule, SecurityContext } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

/**
 * Third-party dependencies
 */
import { AngularEditorModule } from '@kolkov/angular-editor';
import { gfmHeadingId } from "marked-gfm-heading-id";
import { ClipboardModule } from "ngx-clipboard";
import { CookieService } from "ngx-cookie-service";
import { MarkdownModule } from "ngx-markdown";
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TabViewModule } from 'primeng/tabview';


/**
 * Authentication
 */
import { autoLoginGuard } from "@app/core/guards/auto-login.guard";
import { withCredentialsInterceptor } from "@app/core/interceptors/with-credentials.interceptor";
import { AuthApisixService } from "@services/auth/auth-apisix.service";
import { AuthOidcService } from "@services/auth/auth-oidc.service";
import { AuthService } from "@services/auth/auth.service";
import { AutoLoginPartialRoutesGuard } from "angular-auth-oidc-client";
import { OidcIdentityRoleResolver } from './core/resolvers/oidc-identity-role.resolver';


/**
 * Translations
 */
import {
  FileTranslationsLanguageDropdownComponent
} from "@app/tools/translations/file-translations-language-dropdown/file-translations-language-dropdown.component";
import {
  FileTranslationsTranslateContainerComponent
} from "@app/tools/translations/file-translations-manager/file-translations-translate-container/file-translations-translate-container.component";
import {
  FileTranslationsUploadContainerComponent
} from "@app/tools/translations/file-translations-manager/file-translations-upload-container/file-translations-upload-container.component";
import {
  FileTranslationsUploaderComponent
} from "@app/tools/translations/file-translations-manager/file-translations-uploader/file-translations-uploader.component";
import {
  TranslatedDocumentsListComponent
} from "@app/tools/translations/translated-documents-list/translated-documents-list.component";
import {
  DocumentTranslationsMobileNotificationComponent
} from "@app/tools/translations/translations-notifications/document-translations-mobile-notification/document-translations-mobile-notification.component";
import {
  DocumentTranslationsNotificationComponent
} from "@app/tools/translations/translations-notifications/document-translations-notification/document-translations-notification.component";
import {
  InsufficientTextNotificationComponent
} from "@app/tools/translations/translations-notifications/insufficient-text-notification/insufficient-text-notification.component";
import {
  LanguageMismatchNotificationComponent
} from "@app/tools/translations/translations-notifications/language-mismatch-notification/language-mismatch-notification.component";
import {
  PasswordProtectedNotificationComponent
} from "@app/tools/translations/translations-notifications/password-protected-notification/password-protected-notification.component";
import {
  UnsupportedLanguageNotificationComponent
} from "@app/tools/translations/translations-notifications/unsupported-language-notification/unsupported-language-notification.component";
import { TranslationsComponent } from './tools/translations/translations.component';


/**
 * Shared
 */
import { FileUploaderComponent } from "@app/file-uploader/file-uploader.component";
import { featureFlags } from "@environments/environment";
import { GeneAiIconModule } from '@gene-ai/ui/components/icon';
import { ModalService } from "@gene-ai/ui/components/modal/modal.service";
import { DeleteModalComponent } from "@shared/components/delete-modal/delete-modal.component";
import { ToggleChatHistoryDirective } from '@shared/directives/toggle-chat-history/toggle-chat-history.directive';
import { ToggleNavMenuDirective } from '@shared/directives/toggle-nav-menu/toggle-nav-menu.directive';
import { AboutUsComponent } from './about-us/about-us.component';
import { AppComponent } from './app.component';
import { SSEJSClient } from './core/services/server-sent-events/sse-js.client';
import { ServerSentEventsClient } from './core/services/server-sent-events/sse.client';
import { ErrorComponent } from './error/error.component';
import { IframeApjComponent } from './iframe-apj/iframe-apj.component';
import { ImageCreationComponent } from "./image-creation/image-creation.component";
import { LoadingComponent } from './loading/loading.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { MyLibraryMenuOptionComponent } from './navbar-new/my-library-menu-option/my-library-menu-option.component';
import { NavbarNewComponent } from './navbar-new/navbar-new.component';
import { SubMenuOptionComponent } from './navbar-new/sub-menu-option/sub-menu-option.component';
import { SubMenuComponent } from './navbar-new/sub-menu-option/sub-menu/sub-menu.component';
import { ModalReadOnlyPromptComponent } from './prompt/modal-read-only-prompt/modal-read-only-prompt.component';
import { SignOutComponent } from './sign-out/sign-out.component';
import { SigninComponent } from './signin/signin.component';
import { TermsandconditionsComponent } from './termsandconditions/termsandconditions.component';
import { SocialMediaComponent } from './tools/social-media/social-media.component';
import { UserContextMenuComponent } from './user-context-menu/user-context-menu.component';


/**
 * Ask Our Data / Docs
 */
import { LZStringService } from '@services/ask-our-data/lz-string.service';
import { PlotlyService } from '@services/ask-our-data/plotly.service';
import { CardComponent } from "@shared/components/card/card.component";
import { ChatWithDbNewComponent } from './ask-our-data/chat-with-db/chat-with-db-new.component';
import { ChatWithDbComponent } from './ask-our-data/chat-with-db/chat-with-db.component';
import { AskOurDocsChatPageComponent } from './ask-our-docs-v2/ask-our-docs-chat-page/ask-our-docs-chat-page.component';
import { AskOurDocsPageNewComponent } from './ask-our-docs-v2/ask-our-docs-page-new/ask-our-docs-page-new.component';
import {
  AskOurDocsUserManagementAddUsersNewComponent
} from './ask-our-docs-v2/ask-our-docs-user-management-page-new/ask-our-docs-user-management-add-users-new/ask-our-docs-user-management-add-users-new.component';
import {
  AskOurDocsUserManagementPageNewComponent
} from './ask-our-docs-v2/ask-our-docs-user-management-page-new/ask-our-docs-user-management-page-new.component';
import {
  AskOurDocsUserManagementRemoveUsersNewComponent
} from './ask-our-docs-v2/ask-our-docs-user-management-page-new/ask-our-docs-user-management-remove-users-new/ask-our-docs-user-management-remove-users-new.component';
import {
  AdminLandingFeedbackFormComponent
} from './ask-our-docs-v2/components/admin-landing-feedback-form/admin-landing-feedback-form.component';
import {
  AdminPromptLibraryComponent
} from './ask-our-docs-v2/components/admin-prompt-library/admin-prompt-library.component';
import { FeedbackComponent } from './ask-our-docs-v2/components/feedback/feedback.component';
import { AboutRepositoryComponent } from './ask-our-docs-v2/modals/about-repository/about-repository.component';
import {
  AskOurDocsV2ChatHistoryComponent
} from './ask-our-docs-v2/modals/ask-our-docs-v2-chat-history/ask-our-docs-v2-chat-history.component';
import { FeedbackFormComponent } from './ask-our-docs-v2/modals/feedback-form/feedback-form.component';
import { AskOurDocsV2SavePromptComponent } from './ask-our-docs-v2/modals/save-prompt/save-prompt.component';
import { AskOurDocsV2SavedPromptsComponent } from './ask-our-docs-v2/modals/saved-prompts/saved-prompts.component';
import { PromptPageComponent } from './ask-our-docs-v2/prompt-page/prompt-page.component';
import { AskOurDocsChatComponent } from './ask-our-docs/ask-our-docs-chat/ask-our-docs-chat.component';
import { AskOurDocsPageComponent } from './ask-our-docs/ask-our-docs-page/ask-our-docs-page.component';
import {
  AskOurDocsUserManagementAddUsersComponent
} from './ask-our-docs/ask-our-docs-user-management-page/ask-our-docs-user-management-add-users/ask-our-docs-user-management-add-users.component';
import {
  AskOurDocsUserManagementPageComponent
} from './ask-our-docs/ask-our-docs-user-management-page/ask-our-docs-user-management-page.component';
import {
  AskOurDocsUserManagementRemoveUsersComponent
} from './ask-our-docs/ask-our-docs-user-management-page/ask-our-docs-user-management-remove-users/ask-our-docs-user-management-remove-users.component';


/**
 * My Library
 */
import { ChatHistoryNewComponent } from './my-library/chat-history-new/chat-history-new.component';
import {
  ChatHistoryZeroStateComponent
} from './my-library/chat-history-new/chat-history-zero-state/chat-history-zero-state.component';
import {
  ChatHistoryMobileComponent
} from './my-library/chat-history-new/mobile/chat-history-mobile/chat-history-mobile.component';
import { MyLibraryComponent } from './my-library/my-library.component';
import { DataTableComponent } from './my-library/shared/data-table/data-table.component';
import { DrawerComponent as ChatHistoryDrawerComponent } from './my-library/shared/drawer/drawer.component';
import { ActionButtonsComponent } from './my-library/shared/record/action-buttons/action-buttons.component';
import { RecordMobileComponent } from './my-library/shared/record/mobile/record-mobile.component';
import { RecordComponent } from './my-library/shared/record/record.component';
import { MenuOptionNewComponent } from './navbar-new/menu-option-new/menu-option-new.component';


/**
 * Admin Console
 */
import { AdminConsoleComponent } from './admin-console/admin-console.component';
import { FeatureFlagsPanelComponent } from './admin-console/panels/feature-flags-panel/feature-flags-panel.component';
import { FlagGroupComponent } from './admin-console/panels/feature-flags-panel/flag-group/flag-group.component';
import { FlagComponent } from './admin-console/panels/feature-flags-panel/flag/flag.component';


/**
 * App Modules
 */
import { AskOurDataSharedModule } from '@app/ask-our-data/ask-our-data-shared.module';
import { GeneAiUiModule } from '@gene-ai/ui/gene-ai-ui.module';
import { SharedModule } from "@shared/shared.module";
import { AgentsModule } from './agents/agents.module';
import { AppRoutingModule, AutoLoginGuardToken } from './app-routing.module';
import { ChatModule } from './chat/chat.module';
import { CoreModule } from "./core/core.module";


@NgModule({
  declarations: [
    AppComponent,
    SigninComponent,
    SignOutComponent,
    TermsandconditionsComponent,
    ErrorComponent,
    AboutUsComponent,
    ModalReadOnlyPromptComponent,
    FileUploaderComponent,
    IframeApjComponent,
    TranslationsComponent,
    AskOurDocsChatComponent,
    SocialMediaComponent,
    ImageCreationComponent,
    ToggleNavMenuDirective,
    ToggleChatHistoryDirective,
    AskOurDocsPageComponent,
    CardComponent,
    ChatWithDbComponent,
    ChatWithDbNewComponent,
    AskOurDocsUserManagementPageComponent,
    AskOurDocsUserManagementAddUsersComponent,
    AskOurDocsUserManagementRemoveUsersComponent,
    UserContextMenuComponent,
    NavbarNewComponent,
    MaintenanceComponent,
    MyLibraryComponent,
    ChatHistoryNewComponent,
    DataTableComponent,
    RecordComponent,
    MyLibraryMenuOptionComponent,
    ChatHistoryZeroStateComponent,
    ActionButtonsComponent,
    DeleteModalComponent,
    MenuOptionNewComponent,
    SubMenuOptionComponent,
    SubMenuComponent,
    ChatHistoryMobileComponent,
    RecordMobileComponent,
    ChatHistoryDrawerComponent,
    AskOurDocsChatPageComponent,
    PromptPageComponent,
    AskOurDocsPageNewComponent,
    AskOurDocsUserManagementPageNewComponent,
    AskOurDocsUserManagementRemoveUsersNewComponent,
    AskOurDocsUserManagementAddUsersNewComponent,
    CardComponent,
    AboutRepositoryComponent,
    AskOurDocsV2ChatHistoryComponent,
    AskOurDocsV2SavedPromptsComponent,
    AskOurDocsV2SavePromptComponent,
    FeedbackFormComponent,
    DocumentTranslationsNotificationComponent,
    DocumentTranslationsMobileNotificationComponent,
    FeedbackComponent,
    AdminLandingFeedbackFormComponent,
    AdminPromptLibraryComponent,
    LoadingComponent,
    TranslatedDocumentsListComponent,
    AdminPromptLibraryComponent,
    FileTranslationsUploaderComponent,
    FileTranslationsUploadContainerComponent,
    FileTranslationsTranslateContainerComponent,
    FileTranslationsTranslateContainerComponent,
    FileTranslationsLanguageDropdownComponent,
    LanguageMismatchNotificationComponent,
    UnsupportedLanguageNotificationComponent,
    InsufficientTextNotificationComponent,
    PasswordProtectedNotificationComponent,
    AdminConsoleComponent,
    FeatureFlagsPanelComponent,
    FlagComponent,
    FlagGroupComponent,
  ],
  imports: [
    AccordionModule,
    AgentsModule,
    AngularEditorModule,
    AppRoutingModule,
    AskOurDataSharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    ChatModule,
    ClipboardModule,
    CommonModule,
    CoreModule,
    DragDropModule,
    DialogModule,
    FormsModule,
    GeneAiUiModule,
    GeneAiIconModule,
    InputTextModule,
    MarkdownModule.forRoot({
      loader: HttpClient,
      markedExtensions: [gfmHeadingId()],
      sanitize: SecurityContext.NONE
    }),
    ReactiveFormsModule,
    SharedModule,
    TabViewModule,
  ],
  providers: [
    CookieService,
    DatePipe,
    LZStringService,
    ModalService,
    OidcIdentityRoleResolver,
    PlotlyService,
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors(featureFlags.apiSixLogin ? [withCredentialsInterceptor] : [])
    ),
    {
      provide: AuthService,
      useFactory: () => {
        return featureFlags.apiSixLogin ? inject(AuthApisixService) : inject(AuthOidcService)
      }
    },
    {
      provide: ServerSentEventsClient,
      useClass: SSEJSClient
    },
    {
      provide: AutoLoginGuardToken,
      useFactory: () => {
        return featureFlags.apiSixLogin ? autoLoginGuard : inject(AutoLoginPartialRoutesGuard)
      }
    }
  ],
  bootstrap: [AppComponent],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class AppModule { }
